import { BackIconButton } from '@/components/ui/back-icon-button';
import { CloseButton } from '@/components/ui/close-button';
import { TemplateForm } from '@/components/templates/TemplateForm';
import {
  toCreateInput,
  type TemplateFormValues,
} from '@/components/templates/template-form-schema';
import { useAuth } from '@/contexts/auth-context';
import { useCreateTemplateAction } from '@/hooks/actions/use-template-library-actions';
import { Box, Button, HStack, Text } from '@chakra-ui/react';
import type { TemplateLibraryEntry } from '@fluenceur/types';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface SaveAsTemplatePanelProps {
  open: boolean;
  /** Seed values derived from the active composition (pair, language, content). */
  seed: TemplateFormValues;
  onClose: () => void;
  /** Called with the created brand template so the host can auto-select it. */
  onSaved: (entry: TemplateLibraryEntry) => void;
  /**
   * Render a back affordance (chevron) beside the title. Set when the overlay
   * covers a host that had its own context bar (the contact drawer's right
   * panel): back returns to the message. In hosts with no external bar (send
   * modal, bulk drawer, invitation flow) the overlay IS the only header, so the
   * close button alone is enough and the chevron is omitted.
   */
  showBack?: boolean;
}

const FORM_ID = 'save-as-template-form';

/**
 * In-flow "save as template" overlay. Reuses the shared {@link TemplateForm}
 * with the pair locked to the active send mode, so the user only adds a name
 * (and may opt to make it the pair default). Templates belong to the current
 * brand, resolved here so hosts only wire their flow state.
 *
 * Positioning invariant (referenced by every host): this does not portal to the
 * viewport — it fills its nearest positioned ancestor (`inset=0`), so it lays
 * over exactly the surface the active send context owns, and it carries the
 * single header for that region. Hosts must NOT introduce a positioning context
 * between that surface and this overlay, so it also covers any external context
 * bar (e.g. the contact drawer's "New message" bar) — one header, never two
 * stacked. No hard-coded width; the opaque background hides the composer
 * beneath it. Persistence goes through the create action, which invalidates the
 * library cache; the created entry is handed back so the send flow can select
 * it without retyping the body.
 */
const SaveAsTemplatePanel = memo<SaveAsTemplatePanelProps>(
  ({ open, seed, onClose, onSaved, showBack = false }) => {
    const { t } = useTranslation();
    const [attachmentsBlocked, setAttachmentsBlocked] = useState(false);
    const { user } = useAuth();
    const brandId = user?.currentBrand?.id ?? '';
    const createAction = useCreateTemplateAction(brandId);

    if (!open || !brandId) {
      return null;
    }

    const handleSubmit = (values: TemplateFormValues) => {
      createAction.mutate(toCreateInput(values), {
        onSuccess: (entry) => {
          onSaved(entry);
          onClose();
        },
      });
    };

    return (
      <Box
        position="absolute"
        inset={0}
        zIndex={3}
        bg="panelBg"
        display="flex"
        flexDirection="column"
      >
        <HStack px={5} py={4} justify="space-between" flexShrink={0}>
          <HStack gap={2.5} minW={0}>
            {showBack && <BackIconButton aria-label={t('common.back')} onClick={onClose} />}
            <Text fontWeight="semibold" fontSize="md" truncate>
              {t('templates.saveAsTemplate.title')}
            </Text>
          </HStack>
          <CloseButton size="sm" onClick={onClose} />
        </HStack>

        <Box flex={1} minH={0} overflowY="auto" px={5} pb={4}>
          <TemplateForm
            formId={FORM_ID}
            onAttachmentBlockedChange={setAttachmentsBlocked}
            formKey="save-as-template"
            initialValues={seed}
            lockPair
            onSubmit={handleSubmit}
          />
        </Box>

        <HStack px={5} py={4} justify="flex-end" gap={3} flexShrink={0}>
          <Button variant="outline" onClick={onClose} disabled={createAction.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            disabled={attachmentsBlocked}
            loading={createAction.isPending}
          >
            {t('common.save')}
          </Button>
        </HStack>
      </Box>
    );
  }
);

SaveAsTemplatePanel.displayName = 'SaveAsTemplatePanel';

export default SaveAsTemplatePanel;
